import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  ArrowLeft,
  Ban,
  Check,
  Heart,
  MapPin,
  Pencil,
  RefreshCw,
  ShieldCheck,
  Smartphone,
  Star,
  Trash2,
  X,
} from 'lucide-react';
import clsx from 'clsx';

import PageHeader from '@/components/PageHeader';
import Modal from '@/components/Modal';
import { UserStatusBadge } from './AppUsers';
import {
  getPeopleUser,
  removePeopleUserDevices,
  setPeopleUserStatus,
  updatePeopleUser,
  type PeopleUserDetail as PeopleUserDetailDto,
} from '@/lib/operatorApi';
import { formatDate, formatDateTime, formatKop, formatNumber, formatPhone, relativeTime } from '@/lib/format';

// Карточка гостя приложения People: профиль (редактируемый), блокировка,
// внутренняя заметка, устройства с пушами, адреса, избранное, последние
// заказы/брони/отзывы и история действий оператора из AuditLog.

const ORDER_STATUS: Record<PeopleUserDetailDto['orders'][number]['status'], string> = {
  NEW: 'Новый',
  IN_PROGRESS: 'Готовится',
  READY: 'Готов',
  COMPLETED: 'Завершён',
  CANCELLED: 'Отменён',
};

const RESERVATION_STATUS: Record<PeopleUserDetailDto['reservations'][number]['status'], string> = {
  PENDING: 'Ожидает',
  CONFIRMED: 'Подтверждена',
  CANCELLED: 'Отменена',
  COMPLETED: 'Гость пришёл',
  NO_SHOW: 'Не пришёл',
};

const AUDIT_ACTION: Record<string, string> = {
  'app.user.update': 'Изменён профиль',
  'app.user.block': 'Заблокирован',
  'app.user.unblock': 'Разблокирован',
  'app.user.remove_devices': 'Сняты push-подписки',
};

export default function AppUserDetail() {
  const { id } = useParams<{ id: string }>();
  const [user, setUser] = useState<PeopleUserDetailDto | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [modal, setModal] = useState<null | 'edit' | 'block'>(null);
  const [flash, setFlash] = useState<string | null>(null);

  const load = useCallback(() => {
    if (!id) return;
    setLoading(true);
    setError(null);
    getPeopleUser(id)
      .then(setUser)
      .catch((e: Error) => setError(e.message))
      .finally(() => setLoading(false));
  }, [id]);

  useEffect(load, [load]);

  useEffect(() => {
    if (!flash) return;
    const t = window.setTimeout(() => setFlash(null), 3500);
    return () => window.clearTimeout(t);
  }, [flash]);

  const run = async (fn: () => Promise<unknown>, ok: string) => {
    try {
      setBusy(true);
      await fn();
      setModal(null);
      setFlash(ok);
      load();
    } catch (e) {
      window.alert(e instanceof Error ? e.message : 'Ошибка');
    } finally {
      setBusy(false);
    }
  };

  if (loading && !user) {
    return (
      <div className="page-pad text-ink/50">
        <Link to="/app/users" className="link inline-flex items-center gap-1 text-[13px]">
          <ArrowLeft size={14} /> К списку
        </Link>
        <p className="mt-6">Загрузка…</p>
      </div>
    );
  }

  if (error || !user) {
    return (
      <div className="page-pad">
        <Link to="/app/users" className="link inline-flex items-center gap-1 text-[13px]">
          <ArrowLeft size={14} /> К списку
        </Link>
        <p className="mt-6 text-red-600">{error ?? 'Гость не найден'}</p>
      </div>
    );
  }

  const isDeleted = user.status === 'DELETED';
  const isBlocked = user.status === 'BLOCKED';

  return (
    <div>
      <PageHeader
        eyebrow={`Гость People · ${user.id}`}
        title={user.name || 'Без имени'}
        description={[formatPhone(user.phone), user.email].filter((v) => v && v !== '—').join(' · ') || 'Контакты не указаны'}
        actions={
          <>
            <Link to="/app/users" className="btn-ghost">
              <ArrowLeft size={14} strokeWidth={1.75} />
              Назад
            </Link>
            <button type="button" className="btn-ghost" onClick={load} disabled={loading}>
              <RefreshCw size={13} strokeWidth={1.75} />
              Обновить
            </button>
            {!isDeleted && (
              <button type="button" className="btn-outline" onClick={() => setModal('edit')}>
                <Pencil size={14} strokeWidth={1.75} />
                Редактировать
              </button>
            )}
            {!isDeleted &&
              (isBlocked ? (
                <button
                  type="button"
                  className="btn-primary"
                  disabled={busy}
                  onClick={() =>
                    void run(() => setPeopleUserStatus(user.id, 'ACTIVE'), 'Гость разблокирован')
                  }
                >
                  <ShieldCheck size={14} strokeWidth={1.75} />
                  Разблокировать
                </button>
              ) : (
                <button type="button" className="btn-danger" onClick={() => setModal('block')}>
                  <Ban size={14} strokeWidth={1.75} />
                  Заблокировать
                </button>
              ))}
          </>
        }
      />

      <div className="page-pad space-y-6">
        {flash && (
          <div className="flex items-center gap-2 border border-green-600/30 bg-green-50 px-4 py-2 text-[13px] text-green-700">
            <Check size={14} strokeWidth={1.75} />
            {flash}
          </div>
        )}

        {isBlocked && (
          <div className="border border-red-600/30 bg-red-50 px-4 py-3 text-[13px] text-red-700">
            <div className="font-medium">
              Заблокирован {user.blockedAt ? formatDateTime(user.blockedAt) : ''}
            </div>
            <div className="mt-0.5">{user.blockedReason || 'Причина не указана'}</div>
            <div className="mt-1 text-red-700/70">
              Гость не может войти, а активная сессия получает отказ на следующем же запросе.
            </div>
          </div>
        )}
        {isDeleted && (
          <div className="border border-line bg-paper px-4 py-3 text-[13px] text-ink/70">
            Аккаунт удалён самим гостем: контакты стёрты, история заказов сохранена для отчётности.
          </div>
        )}

        <div className="grid gap-6 lg:grid-cols-3">
          <ProfileCard user={user} />
          <StatsCard user={user} />
          <NoteCard
            user={user}
            busy={busy}
            onSave={(note) => run(() => updatePeopleUser(user.id, { adminNote: note }), 'Заметка сохранена')}
          />
        </div>

        <div className="grid gap-6 lg:grid-cols-2">
          <DevicesCard
            user={user}
            busy={busy}
            onRemove={(subId) =>
              run(
                () => removePeopleUserDevices(user.id, subId),
                subId ? 'Устройство отключено от пушей' : 'Все push-подписки сняты',
              )
            }
          />
          <AddressesCard user={user} />
        </div>

        <OrdersCard user={user} />

        <div className="grid gap-6 lg:grid-cols-2">
          <ReservationsCard user={user} />
          <ReviewsCard user={user} />
        </div>

        <div className="grid gap-6 lg:grid-cols-2">
          <FavoritesCard user={user} />
          <AuditCard user={user} />
        </div>
      </div>

      {modal === 'edit' && (
        <EditModal
          user={user}
          busy={busy}
          onClose={() => setModal(null)}
          onSave={(patch) => run(() => updatePeopleUser(user.id, patch), 'Профиль обновлён')}
        />
      )}
      {modal === 'block' && (
        <BlockModal
          busy={busy}
          onClose={() => setModal(null)}
          onConfirm={(reason) =>
            run(() => setPeopleUserStatus(user.id, 'BLOCKED', reason), 'Гость заблокирован')
          }
        />
      )}
    </div>
  );
}

// ─── Карточки ──────────────────────────────────────────────────────

function ProfileCard({ user }: { user: PeopleUserDetailDto }) {
  return (
    <section className="card p-6">
      <h2 className="eyebrow">Профиль</h2>
      <dl className="mt-4 space-y-3 text-[13px]">
        <Row label="Статус">
          <UserStatusBadge status={user.status} />
        </Row>
        <Row label="Имя">{user.name || <span className="text-ink/40">не указано</span>}</Row>
        <Row label="Телефон">{formatPhone(user.phone)}</Row>
        <Row label="Email">{user.email ?? '—'}</Row>
        <Row label="Источник">
          {user.source === 'people' ? (
            <span className="badge-orange">People</span>
          ) : (
            <span className="badge-ink">Web-меню</span>
          )}
          {user.establishment && (
            <Link to={`/tenants/${user.establishment.id}`} className="link ml-2">
              {user.establishment.name}
            </Link>
          )}
        </Row>
        <Row label="Приложение">
          {user.hasPeopleApp ? 'Установлено (есть push-подписка)' : <span className="text-ink/40">нет данных</span>}
        </Row>
        <Row label="Последний вход">
          {user.lastLoginAt ? `${formatDateTime(user.lastLoginAt)} · ${relativeTime(user.lastLoginAt)}` : '—'}
        </Row>
        <Row label="Регистрация">{formatDateTime(user.createdAt)}</Row>
      </dl>
    </section>
  );
}

function Row({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex items-start justify-between gap-4">
      <dt className="shrink-0 text-ink/50">{label}</dt>
      <dd className="min-w-0 text-right text-ink/90 break-words">{children}</dd>
    </div>
  );
}

function StatsCard({ user }: { user: PeopleUserDetailDto }) {
  const s = user.stats;
  const avg = s.completedOrders > 0 ? Math.round(s.spentTotal / s.completedOrders) : 0;
  return (
    <section className="card p-6">
      <h2 className="eyebrow">Активность</h2>
      <div className="mt-4 grid grid-cols-2 gap-4">
        <div>
          <div className="kpi-value">{formatKop(s.spentTotal)}</div>
          <div className="mt-2 text-[11px] text-ink/50">Потрачено (завершённые)</div>
        </div>
        <div>
          <div className="kpi-value">
            {formatNumber(s.completedOrders)}
            <span className="text-[14px] font-normal text-ink/40">/{formatNumber(s.orders)}</span>
          </div>
          <div className="mt-2 text-[11px] text-ink/50">Заказов завершено / всего</div>
        </div>
        <div>
          <div className="kpi-value">{avg ? formatKop(avg) : '—'}</div>
          <div className="mt-2 text-[11px] text-ink/50">Средний чек</div>
        </div>
        <div>
          <div className="kpi-value">{formatNumber(s.reservations)}</div>
          <div className="mt-2 text-[11px] text-ink/50">Броней</div>
        </div>
        <div>
          <div className="kpi-value">{formatNumber(s.reviews)}</div>
          <div className="mt-2 text-[11px] text-ink/50">Отзывов</div>
        </div>
        <div>
          <div className="kpi-value">{formatNumber(s.favorites)}</div>
          <div className="mt-2 text-[11px] text-ink/50">В избранном</div>
        </div>
      </div>
      <div className="mt-4 text-[12px] text-ink/50">
        Последний заказ: {s.lastOrderAt ? formatDateTime(s.lastOrderAt) : '—'}
      </div>
    </section>
  );
}

function NoteCard({
  user,
  busy,
  onSave,
}: {
  user: PeopleUserDetailDto;
  busy: boolean;
  onSave: (note: string | null) => void;
}) {
  const [value, setValue] = useState(user.adminNote ?? '');
  useEffect(() => setValue(user.adminNote ?? ''), [user.adminNote]);
  const dirty = value.trim() !== (user.adminNote ?? '').trim();

  return (
    <section className="card p-6">
      <h2 className="eyebrow">Заметка оператора</h2>
      <p className="mt-1 text-[12px] text-ink/50">Видна только в суперадминке. Гость её не увидит.</p>
      <textarea
        className="input mt-3 min-h-[120px] resize-y"
        value={value}
        maxLength={2000}
        placeholder="Например: жаловался на курьера 12.09, компенсирован промокод…"
        onChange={(e) => setValue(e.target.value)}
      />
      <div className="mt-3 flex items-center justify-between">
        <span className="text-[11px] text-ink/40">{value.length}/2000</span>
        <button
          type="button"
          className="btn-dark"
          disabled={!dirty || busy}
          onClick={() => onSave(value.trim() ? value : null)}
        >
          Сохранить
        </button>
      </div>
    </section>
  );
}

function DevicesCard({
  user,
  busy,
  onRemove,
}: {
  user: PeopleUserDetailDto;
  busy: boolean;
  onRemove: (subscriptionId?: string) => void;
}) {
  return (
    <section className="card p-6">
      <div className="flex items-center justify-between gap-3">
        <h2 className="eyebrow">Устройства и push</h2>
        {user.devices.length > 1 && (
          <button
            type="button"
            className="btn-ghost text-red-600 !px-2 !py-1 text-[12px]"
            disabled={busy}
            onClick={() => {
              if (window.confirm('Снять push-подписки со всех устройств гостя?')) onRemove();
            }}
          >
            Снять все
          </button>
        )}
      </div>
      {user.devices.length === 0 ? (
        <p className="mt-4 text-[13px] text-ink/50">
          Push-подписок нет: гость не разрешил уведомления или выходил из аккаунта.
        </p>
      ) : (
        <ul className="mt-4 divide-y divide-line text-[13px]">
          {user.devices.map((d) => (
            <li key={d.id} className="flex items-start justify-between gap-3 py-3">
              <div className="flex min-w-0 items-start gap-3">
                <Smartphone size={16} strokeWidth={1.75} className="mt-0.5 shrink-0 text-ink/40" />
                <div className="min-w-0">
                  <div className="font-medium">
                    {d.deviceModel || d.platform}
                    {d.appVersion && <span className="ml-2 font-normal text-ink/50">v{d.appVersion}</span>}
                  </div>
                  <div className="text-[12px] text-ink/50">
                    {d.platform} · {d.appBundleId ?? '—'}
                    {d.locale ? ` · ${d.locale}` : ''}
                  </div>
                  <div className="text-[12px] text-ink/50">
                    Активность: {relativeTime(d.lastUsedAt)} · с {formatDate(d.createdAt)}
                  </div>
                </div>
              </div>
              <button
                type="button"
                className="btn-ghost !px-2 !py-1 text-ink/50 hover:text-red-600"
                disabled={busy}
                aria-label="Отключить устройство от пушей"
                onClick={() => {
                  if (window.confirm('Отключить это устройство от push-уведомлений?')) onRemove(d.id);
                }}
              >
                <Trash2 size={14} strokeWidth={1.75} />
              </button>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function AddressesCard({ user }: { user: PeopleUserDetailDto }) {
  return (
    <section className="card p-6">
      <h2 className="eyebrow">Сохранённые адреса</h2>
      {user.addresses.length === 0 ? (
        <p className="mt-4 text-[13px] text-ink/50">Адресов нет.</p>
      ) : (
        <ul className="mt-4 divide-y divide-line text-[13px]">
          {user.addresses.map((a) => (
            <li key={a.id} className="flex items-start gap-3 py-3">
              <MapPin size={16} strokeWidth={1.75} className="mt-0.5 shrink-0 text-ink/40" />
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-medium">{a.title}</span>
                  {a.isDefault && <span className="badge-ink">По умолчанию</span>}
                </div>
                <div className="text-ink/80">{a.address}</div>
                <div className="text-[12px] text-ink/50">
                  {[
                    a.entrance && `подъезд ${a.entrance}`,
                    a.floor && `этаж ${a.floor}`,
                    a.apartment && `кв. ${a.apartment}`,
                    a.comment,
                  ]
                    .filter(Boolean)
                    .join(' · ')}
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function OrdersCard({ user }: { user: PeopleUserDetailDto }) {
  return (
    <section className="card">
      <div className="flex items-center justify-between px-6 pt-6">
        <h2 className="eyebrow">Последние заказы</h2>
        <span className="text-[12px] text-ink/50">показаны {user.orders.length} из {user.stats.orders}</span>
      </div>
      {user.orders.length === 0 ? (
        <p className="px-6 py-6 text-[13px] text-ink/50">Заказов пока нет.</p>
      ) : (
        <div className="table-scroll mt-4">
          <table className="table-grid">
            <thead>
              <tr>
                <th>№</th>
                <th>Заведение</th>
                <th>Тип</th>
                <th>Статус</th>
                <th className="text-right">Сумма</th>
                <th>Создан</th>
                <th>Завершён</th>
              </tr>
            </thead>
            <tbody>
              {user.orders.map((o) => (
                <tr key={o.id}>
                  <td className="font-mono text-[12px]">#{o.number}</td>
                  <td>
                    <Link to={`/tenants/${o.establishment.id}`} className="link">
                      {o.establishment.name}
                    </Link>
                  </td>
                  <td className="text-ink/70">
                    {o.deliveryAddress ? 'Доставка' : o.source === 'APP' ? 'В заведении' : 'Касса'}
                    {o.paidFromApp && <span className="ml-2 badge-green">Оплачен онлайн</span>}
                  </td>
                  <td>
                    <span
                      className={clsx(
                        o.status === 'COMPLETED' && 'badge-green',
                        o.status === 'CANCELLED' && 'badge-red',
                        (o.status === 'NEW' || o.status === 'IN_PROGRESS' || o.status === 'READY') && 'badge-amber',
                      )}
                    >
                      {ORDER_STATUS[o.status]}
                    </span>
                  </td>
                  <td className="text-right font-mono text-[12px]">{formatKop(o.total)}</td>
                  <td className="text-ink/70">{formatDateTime(o.createdAt)}</td>
                  <td className="text-ink/70">{o.completedAt ? formatDateTime(o.completedAt) : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

function ReservationsCard({ user }: { user: PeopleUserDetailDto }) {
  return (
    <section className="card p-6">
      <div className="flex items-center justify-between">
        <h2 className="eyebrow">Брони столов</h2>
        <span className="text-[12px] text-ink/50">
          показаны {user.reservations.length} из {user.stats.reservations}
        </span>
      </div>
      {user.reservations.length === 0 ? (
        <p className="mt-4 text-[13px] text-ink/50">Броней нет.</p>
      ) : (
        <ul className="mt-4 divide-y divide-line text-[13px]">
          {user.reservations.map((r) => (
            <li key={r.id} className="py-3">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="font-medium">
                  {formatDateTime(r.startsAt)} · {r.guests} {pluralGuests(r.guests)}
                </div>
                <span
                  className={clsx(
                    r.status === 'CONFIRMED' && 'badge-green',
                    r.status === 'PENDING' && 'badge-amber',
                    (r.status === 'CANCELLED' || r.status === 'NO_SHOW') && 'badge-red',
                    r.status === 'COMPLETED' && 'badge-ink',
                  )}
                >
                  {RESERVATION_STATUS[r.status]}
                </span>
              </div>
              <div className="text-[12px] text-ink/50">
                <Link to={`/tenants/${r.establishment.id}`} className="link">
                  {r.establishment.name}
                </Link>
                {r.comment && ` · ${r.comment}`}
              </div>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function ReviewsCard({ user }: { user: PeopleUserDetailDto }) {
  return (
    <section className="card p-6">
      <div className="flex items-center justify-between">
        <h2 className="eyebrow">Отзывы</h2>
        <Link to="/app/reviews" className="link text-[12px]">
          Модерация
        </Link>
      </div>
      {user.reviews.length === 0 ? (
        <p className="mt-4 text-[13px] text-ink/50">Отзывов нет.</p>
      ) : (
        <ul className="mt-4 divide-y divide-line text-[13px]">
          {user.reviews.map((r) => (
            <li key={r.id} className={clsx('py-3', r.isHidden && 'opacity-60')}>
              <div className="flex flex-wrap items-center gap-2">
                <span className="flex items-center gap-0.5" aria-label={`Оценка ${r.rating} из 5`}>
                  {[1, 2, 3, 4, 5].map((n) => (
                    <Star
                      key={n}
                      size={12}
                      strokeWidth={1.75}
                      className={n <= r.rating ? 'fill-amber-400 text-amber-400' : 'text-ink/20'}
                    />
                  ))}
                </span>
                <Link to={`/tenants/${r.establishment.id}`} className="link text-[12px]">
                  {r.establishment.name}
                </Link>
                <span className="text-[12px] text-ink/50">{formatDateTime(r.createdAt)}</span>
                {r.isHidden && <span className="badge-ink">Скрыт</span>}
              </div>
              {r.text ? (
                <p className="mt-1 whitespace-pre-line text-ink/90">{r.text}</p>
              ) : (
                <p className="mt-1 italic text-ink/40">Без текста</p>
              )}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function FavoritesCard({ user }: { user: PeopleUserDetailDto }) {
  return (
    <section className="card p-6">
      <h2 className="eyebrow">Избранные заведения</h2>
      {user.favorites.length === 0 ? (
        <p className="mt-4 text-[13px] text-ink/50">Пусто.</p>
      ) : (
        <ul className="mt-4 divide-y divide-line text-[13px]">
          {user.favorites.map((f) => (
            <li key={f.establishment.id} className="flex items-center justify-between gap-3 py-2.5">
              <div className="flex items-center gap-2">
                <Heart size={14} strokeWidth={1.75} className="fill-red-500 text-red-500" />
                <Link to={`/tenants/${f.establishment.id}`} className="link">
                  {f.establishment.name}
                </Link>
                {!f.establishment.showInApp && <span className="badge-ink">скрыто в приложении</span>}
              </div>
              <span className="text-[12px] text-ink/50">{formatDate(f.createdAt)}</span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function AuditCard({ user }: { user: PeopleUserDetailDto }) {
  return (
    <section className="card p-6">
      <h2 className="eyebrow">История действий оператора</h2>
      {user.audit.length === 0 ? (
        <p className="mt-4 text-[13px] text-ink/50">Действий не было.</p>
      ) : (
        <ul className="mt-4 divide-y divide-line text-[13px]">
          {user.audit.map((a) => (
            <li key={a.id} className="py-2.5">
              <div className="flex flex-wrap items-baseline justify-between gap-2">
                <span className="font-medium">{AUDIT_ACTION[a.action] ?? a.action}</span>
                <span className="text-[12px] text-ink/50">{formatDateTime(a.createdAt)}</span>
              </div>
              <div className="text-[12px] text-ink/50">
                {a.operator?.name || a.operator?.email || 'система'}
                {describeAuditMeta(a.action, a.meta)}
              </div>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function describeAuditMeta(action: string, meta: Record<string, unknown>): string {
  if (action === 'app.user.block' && typeof meta.reason === 'string' && meta.reason) {
    return ` · ${meta.reason}`;
  }
  if (action === 'app.user.update' && meta.changed && typeof meta.changed === 'object') {
    const fields = Object.keys(meta.changed as Record<string, unknown>)
      .map((f) => ({ name: 'имя', phone: 'телефон', email: 'email', adminNote: 'заметка' })[f] ?? f)
      .join(', ');
    return fields ? ` · ${fields}` : '';
  }
  if (action === 'app.user.remove_devices' && typeof meta.removed === 'number') {
    return ` · ${meta.removed}`;
  }
  return '';
}

function pluralGuests(n: number): string {
  const mod10 = n % 10;
  const mod100 = n % 100;
  if (mod10 === 1 && mod100 !== 11) return 'гость';
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 10 || mod100 >= 20)) return 'гостя';
  return 'гостей';
}

// ─── Модалки ───────────────────────────────────────────────────────

function EditModal({
  user,
  busy,
  onClose,
  onSave,
}: {
  user: PeopleUserDetailDto;
  busy: boolean;
  onClose: () => void;
  onSave: (patch: { name?: string | null; phone?: string | null; email?: string | null }) => void;
}) {
  const [name, setName] = useState(user.name ?? '');
  const [phone, setPhone] = useState(user.phone ?? '');
  const [email, setEmail] = useState(user.email ?? '');

  const patch: { name?: string | null; phone?: string | null; email?: string | null } = {};
  if (name.trim() !== (user.name ?? '')) patch.name = name.trim() || null;
  if (phone.trim() !== (user.phone ?? '')) patch.phone = phone.trim() || null;
  if (email.trim().toLowerCase() !== (user.email ?? '')) patch.email = email.trim() || null;
  const dirty = Object.keys(patch).length > 0;
  const noContact = !phone.trim() && !email.trim();

  return (
    <Modal title="Редактировать профиль" onClose={onClose}>
      <form
        className="space-y-4"
        onSubmit={(e) => {
          e.preventDefault();
          if (dirty && !noContact) onSave(patch);
        }}
      >
        <label className="block text-[13px]">
          <span className="text-ink/60">Имя</span>
          <input className="input mt-1" value={name} maxLength={120} onChange={(e) => setName(e.target.value)} />
        </label>
        <label className="block text-[13px]">
          <span className="text-ink/60">Телефон</span>
          <input
            className="input mt-1"
            value={phone}
            inputMode="tel"
            placeholder="+7 999 123-45-67"
            onChange={(e) => setPhone(e.target.value)}
          />
        </label>
        <label className="block text-[13px]">
          <span className="text-ink/60">Email</span>
          <input
            className="input mt-1"
            value={email}
            inputMode="email"
            autoCapitalize="off"
            placeholder="guest@example.com"
            onChange={(e) => setEmail(e.target.value)}
          />
        </label>
        <p className="text-[12px] text-ink/50">
          Телефон и email — это логины гостя (вход по коду). Новый контакт не должен быть занят другим
          активным гостем; хотя бы один контакт обязателен.
        </p>
        {noContact && (
          <p className="text-[12px] text-red-600">Нельзя оставить гостя без телефона и email.</p>
        )}
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" className="btn-outline" onClick={onClose} disabled={busy}>
            <X size={14} strokeWidth={1.75} />
            Отмена
          </button>
          <button type="submit" className="btn-dark" disabled={!dirty || noContact || busy}>
            <Check size={14} strokeWidth={1.75} />
            Сохранить
          </button>
        </div>
      </form>
    </Modal>
  );
}

function BlockModal({
  busy,
  onClose,
  onConfirm,
}: {
  busy: boolean;
  onClose: () => void;
  onConfirm: (reason: string) => void;
}) {
  const [reason, setReason] = useState('');
  return (
    <Modal title="Заблокировать гостя" onClose={onClose}>
      <form
        className="space-y-4"
        onSubmit={(e) => {
          e.preventDefault();
          onConfirm(reason);
        }}
      >
        <p className="text-[13px] text-ink/70">
          Гость не сможет войти в приложение и web-меню, активные сессии получат отказ. Оформленные
          заказы и брони не отменяются — при необходимости заведение делает это само. Блокировку
          можно снять в любой момент.
        </p>
        <label className="block text-[13px]">
          <span className="text-ink/60">Причина (видна только операторам)</span>
          <textarea
            className="input mt-1 min-h-[90px] resize-y"
            value={reason}
            maxLength={500}
            placeholder="Например: злоупотребление отменами заказов"
            onChange={(e) => setReason(e.target.value)}
          />
        </label>
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" className="btn-outline" onClick={onClose} disabled={busy}>
            Отмена
          </button>
          <button type="submit" className="btn-danger" disabled={busy}>
            <Ban size={14} strokeWidth={1.75} />
            Заблокировать
          </button>
        </div>
      </form>
    </Modal>
  );
}
